// Guarded "create the default Control Strategy config for a fullKey" used by the
// chart and print pages. Their effects re-run several times while a fullKey's
// config list is empty (deps change), and each run awaited a data-exists check
// before creating, so two runs could both create a default ~50ms apart —
// leaving duplicate configs whose UI/calc selection then disagreed (Colonial
// Linden D6378 PABS). This creates at most one default per fullKey:
//   - concurrent calls for a fullKey share one in-flight promise;
//   - right before creating, it re-reads the configs and only creates when the
//     read succeeds AND is empty (a failed read never triggers a create).
//
// deps: { doesDataExist(fullKey), fetchConfigs(fullKey) -> array|null, create(data) }
export function createDefaultControlStrategyConfigEnsurer({ doesDataExist, fetchConfigs, create }) {
  const inFlight = new Map();

  const run = async (fullKey, defaults) => {
    const hasData = await doesDataExist(fullKey);
    if (!hasData) return false;
    const existing = await fetchConfigs(fullKey);
    if (!Array.isArray(existing) || existing.length > 0) return false;
    await create({ ...defaults, fullKey });
    return true;
  };

  return function ensureDefaultControlStrategyConfig(fullKey, defaults) {
    if (!fullKey) return Promise.resolve(false);
    if (inFlight.has(fullKey)) return inFlight.get(fullKey);
    const promise = run(fullKey, defaults).finally(() => inFlight.delete(fullKey));
    inFlight.set(fullKey, promise);
    return promise;
  };
}
